import { motion } from 'framer-motion'
import { t } from 'i18next'
import React from 'react'
import { useForm } from 'react-hook-form'
import { useEtracker } from '../../../hooks/use-etracker'
import { Button } from '../../button/button'
import { RadioButtonGroup } from '../../radio-button-group/radio-button-group'

import { TextArea } from '../../text-area/text-area'
import styles from '../exit-feedback-modal.module.css'

interface FinishedExitFormProps {
	onSubmit: () => void
	exitButtonSlot?: React.ReactNode
}

export const FinishedExitForm: React.FC<FinishedExitFormProps> = ({ onSubmit, exitButtonSlot }) => {
	const {
		register,
		handleSubmit,
		watch,
		control,
		formState: { errors },
	} = useForm()
	const { trackExitEvent } = useEtracker()

	const watchedExitReason = watch('exit-reason')

	const options = [
		{
			value: 'notSuitable',
			label: t('exitFeedback.formFinished.reason.options.notSuitable'),
			append: (
				<motion.div
					animate={{
						opacity: watchedExitReason === 'notSuitable' ? 1 : 0,
						height: watchedExitReason === 'notSuitable' ? 'auto' : '0px',
					}}
					transition={{ duration: 0.3, ease: 'easeInOut' }}
					style={{ overflow: 'hidden' }}
				>
					<TextArea
						control={control}
						name={'not-suitable-explanation'}
						placeholder={t('exitFeedback.formFinished.reason.options.notSuitablePlaceholder')}
						disabled={watchedExitReason !== 'notSuitable'}
					/>
				</motion.div>
			),
		},
		{
			value: 'suitableButNotInterested',
			label: t('exitFeedback.formFinished.reason.options.suitableButNotInterested'),
		},
		{
			value: 'comeBackLater',
			label: t('exitFeedback.formFinished.reason.options.comeBackLater'),
		},
		{
			value: 'other',
			label: t('exitFeedback.formFinished.reason.options.other'),
			append: (
				<motion.div
					animate={{
						opacity: watchedExitReason === 'other' ? 1 : 0,
						height: watchedExitReason === 'other' ? 'auto' : '0px',
					}}
					transition={{ duration: 0.3, ease: 'easeInOut' }}
					style={{ overflow: 'hidden' }}
				>
					<TextArea
						registerOptions={{ required: t('exitFeedback.fieldRequired') }}
						control={control}
						error={errors['other-explanation'] && t('exitFeedback.fieldRequired')}
						name={'other-explanation'}
						placeholder={t('exitFeedback.formFinished.reason.options.otherPlaceholder')}
						disabled={watchedExitReason !== 'other'}
					/>
				</motion.div>
			),
		},
	]

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const handleFormSubmit = (data: any) => {
		const exitValue = data['exit-reason']
		const translatedExitValue = options.find((option) => option.value === exitValue)?.label || exitValue

		let answer = translatedExitValue

		// Add explanations for options that have text areas
		if (exitValue === 'notSuitable' && data['not-suitable-explanation']) {
			answer += `: ${data['not-suitable-explanation'] || ''}`
		} else if (exitValue === 'other' && data['other-explanation']) {
			answer += `: ${data['other-explanation'] || ''}`
		}

		trackExitEvent(t('exitFeedback.formFinished.reason.label'), answer)

		if (onSubmit) {
			onSubmit()
		}
	}

	return (
		<form className={styles.form} onSubmit={handleSubmit(handleFormSubmit)}>
			<div className={styles.fields}>
				<RadioButtonGroup
					name="exit-reason"
					label={t('exitFeedback.formFinished.reason.label')}
					options={options}
					className={styles.formGroup}
					register={register}
					registerOptions={{ required: t('exitFeedback.fieldRequired') }}
					error={errors['exit-reason'] && t('exitFeedback.fieldRequired')}
				/>
			</div>

			<div className={styles.buttons}>
				{exitButtonSlot}
				<Button className={styles.submit} type="primary" submit={true}>
					{t('exitFeedback.submit')}
				</Button>
			</div>
		</form>
	)
}
